/**
 * Core-100 R4 wave 2 — CORE-O (Rel): one launch act is one learning-history row.
 *
 * The launch INSERT carries a `launch_key` generated once per launch act. The database refuses a second row with the
 * same (technician_profile_id, launch_key) (23505, index training_consumption_sessions_launch_key_uq, migration
 * 20270206021900), so a retried request — a transient network failure, a double-submit — reuses the row the first
 * attempt wrote instead of adding a second "opened" entry. A retired training is refused by the server
 * (TRAINING_ARTIFACT_RETIRED) and is never retried.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

export const LAUNCH_KEY_INDEX = 'training_consumption_sessions_launch_key_uq';

export interface LaunchSessionRow {
  technician_profile_id: string;
  artifact_id: string;
  launch_key: string;
  [column: string]: unknown;
}

export type LaunchSessionResult =
  | { ok: true; sessionId: string; reused: boolean }
  | { ok: false; retired: boolean; message: string };

interface PgErrorLike { code?: string; message?: string }

const isDuplicateLaunch = (e: PgErrorLike | null | undefined) =>
  !!e && e.code === '23505' && (e.message ?? '').includes(LAUNCH_KEY_INDEX);
const isRetired = (e: PgErrorLike | null | undefined) => (e?.message ?? '').includes('TRAINING_ARTIFACT_RETIRED');
/** A transport failure carries no PostgreSQL code: the insert may or may not have committed. */
const isTransient = (e: PgErrorLike | null | undefined) => !!e && !e.code;

export function newLaunchKey(): string {
  return globalThis.crypto.randomUUID();
}

async function readBack(client: SupabaseClient, row: LaunchSessionRow): Promise<string | null> {
  const { data } = await client
    .from('training_consumption_sessions')
    .select('id')
    .eq('technician_profile_id', row.technician_profile_id)
    .eq('launch_key', row.launch_key)
    .maybeSingle();
  return (data as { id?: string } | null)?.id ?? null;
}

/**
 * Insert the launch's session once. `attempts` bounds retries of a transient failure (same key each time).
 */
export async function insertLaunchSessionOnce(
  client: SupabaseClient, row: LaunchSessionRow, attempts = 2,
): Promise<LaunchSessionResult> {
  let last: PgErrorLike | null = null;
  for (let i = 0; i < Math.max(1, attempts); i += 1) {
    const { data, error } = await client.from('training_consumption_sessions').insert(row).select('id').single();
    if (!error && data) return { ok: true, sessionId: (data as { id: string }).id, reused: false };
    last = error as PgErrorLike;
    if (isRetired(last)) return { ok: false, retired: true, message: last?.message ?? '' };
    if (isDuplicateLaunch(last)) {
      const id = await readBack(client, row);
      if (id) return { ok: true, sessionId: id, reused: true };
      break;
    }
    if (!isTransient(last)) break;
    // transient: the first attempt may have committed — look before inserting again
    const id = await readBack(client, row);
    if (id) return { ok: true, sessionId: id, reused: true };
  }
  return { ok: false, retired: false, message: last?.message ?? 'launch session not recorded' };
}
